import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Link, useLocation } from 'react-router-dom';
import casInstance, { caversion } from '../config/Api';
import { AuthConfig as authconfig } from '../config/sso.config.ts';

const SafaBooking = () => {
  const memberId = localStorage.getItem('member_id');
  const location = useLocation();
  const verifiedStorageKey = memberId
    ? `bookingOtpVerified:${memberId}`
    : 'bookingOtpVerified:guest';
  const [isVerified, setIsVerified] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [enrolmentInput, setEnrolmentInput] = useState('');
  const [memberData, setMemberData] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [verifiedMember, setVerifiedMember] = useState(() => {
    const stored = localStorage.getItem('bookingOtpMember');
    return stored ? JSON.parse(stored) : null;
  });
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [bookingLoadingId, setBookingLoadingId] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentInfoByEvent, setPaymentInfoByEvent] = useState({});
  const [paymentInfoLoading, setPaymentInfoLoading] = useState(false);
  const [paymentInfoError, setPaymentInfoError] = useState('');
  const paymentFetchKeyRef = useRef(new Set());

  const paymentParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('payment_status')) return null;
    return {
      status: params.get('payment_status'),
      transactionId: params.get('transaction_id'),
      participantId: params.get('participant_id'),
      eventId: params.get('event_id'),
      memberRegNo: params.get('member_reg_no'),
      bookingStatus: params.get('booking_status'),
      paymentState: params.get('payment_state'),
      paidAmount: params.get('paid_amount'),
      currency: params.get('currency'),
      message: params.get('message')
    };
  }, [location.search]);

  useEffect(() => {
    if (!verifiedStorageKey) {
      setIsVerified(false);
      return;
    }
    setIsVerified(localStorage.getItem(verifiedStorageKey) === '1');
  }, [memberId, verifiedStorageKey]);

  useEffect(() => {
    if (!memberId) return;
    if (!isVerified) {
      setIsModalOpen(true);
    }
  }, [memberId, isVerified]);

  useEffect(() => {
    if (!isVerified) return;
    const memberRegNo =
      enrolmentInput ||
      (verifiedMember && verifiedMember.enrolmentNo) ||
      (memberData && memberData.enrolmentNo) ||
      (paymentParams && paymentParams.memberRegNo);
    const fetchEvents = async () => {
      setEventsLoading(true);
      setEventsError('');
      try {
        const { data } = await casInstance.get(`${caversion}events`, {
          params: memberRegNo ? { member_reg_no: memberRegNo } : undefined
        });
        if (data && data.success) {
          setEvents(Array.isArray(data.data) ? data.data : []);
        } else {
          setEvents([]);
          setEventsError(data && data.message ? data.message : 'Unable to load events.');
        }
      } catch (error) {
        setEventsError('Unable to load events right now. Please try again.');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [isVerified, enrolmentInput, verifiedMember, memberData, paymentParams]);

  useEffect(() => {
    if (!paymentParams) return;
    setPaymentStatus(paymentParams);
  }, [paymentParams]);

  useEffect(() => {
    if (eventsLoading || events.length === 0) {
      return;
    }

    const enrolmentNo =
      enrolmentInput ||
      (verifiedMember && verifiedMember.enrolmentNo) ||
      (memberData && memberData.enrolmentNo) ||
      (paymentParams && paymentParams.memberRegNo);
    if (!enrolmentNo) return;

    const fetchPaymentInfo = async (eventId) => {
      setPaymentInfoLoading(true);
      setPaymentInfoError('');
      try {
        const { data } = await casInstance.get(`${caversion}events/${eventId}/payment`, {
          params: { enrolmentNo }
        });
        if (data && data.success) {
          setPaymentInfoByEvent((prev) => ({ ...prev, [eventId]: data.data || null }));
        }
      } catch (error) {
        setPaymentInfoError('Unable to load payment status right now. Please try again.');
      } finally {
        setPaymentInfoLoading(false);
      }
    };

    events.forEach((eventItem) => {
      const fetchKey = `${eventItem.id}:${enrolmentNo}`;
      if (paymentFetchKeyRef.current.has(fetchKey)) return;
      paymentFetchKeyRef.current.add(fetchKey);
      fetchPaymentInfo(eventItem.id);
    });
  }, [eventsLoading, events, enrolmentInput, verifiedMember, memberData, paymentParams]);

  const handleFetchMember = async (event) => {
    event.preventDefault();
    if (!enrolmentInput || enrolmentInput.length < 4) {
      setMemberError('Please enter your 4 digit enrolment number.');
      return;
    }
    setMemberLoading(true);
    setMemberError('');
    setMemberData(null);
    setOtpSent(false);
    setOtpError('');
    try {
      const { data } = await casInstance.get(`${caversion}get-member-by-username`, {
        params: { username: enrolmentInput }
      });
      if (data && data.success && data.data) {
        setMemberData(data.data);
      } else {
        setMemberError(data && data.message ? data.message : 'Member not found.');
      }
    } catch (error) {
      setMemberError('We could not find your member record. Please try again.');
    } finally {
      setMemberLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!memberData || !memberData.email || !memberData.mobile) {
      setOtpError('Member email or mobile is missing. Please contact ICAB support.');
      return;
    }
    setOtpSending(true);
    setOtpError('');
    try {
      const { data } = await casInstance.post(`${caversion}send-otp`, {
        user_email: memberData.email,
        user_mobile: memberData.mobile
      });
      if (data && data.success) {
        setOtpSent(true);
        setOtpExpiresAt(data.data && data.data.expires_at ? data.data.expires_at : '');
      } else {
        setOtpError(data && data.message ? data.message : 'Unable to send OTP.');
      }
    } catch (error) {
      setOtpError('Unable to send OTP right now. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otpCode || otpCode.length < 4) {
      setOtpError('Please enter the OTP.');
      return;
    }
    setOtpVerifying(true);
    setOtpError('');
    try {
      const { data } = await casInstance.post(`${caversion}verify-otp`, {
        user_email: memberData.email,
        otp: otpCode
      });
      if (data && data.success) {
        if (verifiedStorageKey) {
          localStorage.setItem(verifiedStorageKey, '1');
        }
        localStorage.setItem('bookingOtpMember', JSON.stringify(memberData));
        setVerifiedMember(memberData);
        setIsVerified(true);
        setIsModalOpen(false);
        setIsLoginModalOpen(false);
      } else {
        setOtpError(data && data.message ? data.message : 'OTP verification failed.');
      }
    } catch (error) {
      setOtpError('OTP verification failed. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    const parsed = new Date(dateString.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
      return dateString;
    }
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleBookEvent = async (eventItem) => {
    setBookingMessage('');
    setBookingError('');
    setPaymentUrl('');
    setPaymentReference('');
    setPaymentInfoByEvent((prev) => ({ ...prev, [eventItem.id]: null }));
    const enrolmentNo =
      enrolmentInput ||
      (verifiedMember && verifiedMember.enrolmentNo) ||
      (memberData && memberData.enrolmentNo);
    if (!enrolmentNo) {
      setBookingError('Missing enrolment number. Please verify again.');
      return;
    }
    const confirmed = window.confirm(
      `Confirm registration for "${eventItem.name}" with enrolment no ${enrolmentNo}?`
    );
    if (!confirmed) return;
    setBookingLoadingId(eventItem.id);
    try {
      const { data } = await casInstance.post(
        `${caversion}events/${eventItem.id}/participants`,
        { enrolmentNo }
      );
      if (data && data.success) {
        const payment = data.data && data.data.payment;
        const participant = data.data && data.data.participant;
        if (participant && participant.payment_status === 'PAID') {
          setBookingMessage(data.message || 'Registration submitted successfully.');
          setPaymentInfoByEvent((prev) => ({ ...prev, [eventItem.id]: data.data || null }));
          return;
        } else if (payment && payment.required && payment.session_url) {
          setBookingMessage('Registration confirmed. Redirecting to the payment gateway...');
          setPaymentUrl(payment.session_url);
          setPaymentReference(payment.transaction_id || '');
          setTimeout(() => {
            window.location.assign(payment.session_url);
          }, 1000);
        } else {
          setBookingMessage(data.message || 'Registration submitted successfully.');
        }
      } else {
        setBookingError(data && data.message ? data.message : 'Registration failed.');
      }
    } catch (error) {
      setBookingError('Registration failed. Please try again.');
    } finally {
      setBookingLoadingId(null);
    }
  };

  const getPaymentCta = (eventId) => {
    const paymentInfo = paymentInfoByEvent[eventId];
    if (!paymentInfo || !paymentInfo.participant || !paymentInfo.payment) return null;
    const paymentStatus = paymentInfo.participant.payment_status;
    const paymentState = paymentInfo.participant.payment_state;
    const sessionUrl = paymentInfo.payment.session_url;
    if (paymentStatus === 'PAID') return null;
    if (!sessionUrl) return null;
    const label =
      paymentState === 'FAILED' || paymentState === 'CANCELLED'
        ? 'Retry Payment'
        : 'Proceed to Payment';
    return { label, sessionUrl };
  };

  const handleLogout = () => {
    const hasSsoToken =
      Cookies.get('access_token') || Cookies.get('login_token') || localStorage.getItem('token');
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('member_id');
      localStorage.removeItem('member_pass');
      localStorage.removeItem('bookingOtpMember');
      if (verifiedStorageKey) {
        localStorage.removeItem(verifiedStorageKey);
      }
      Cookies.remove('access_token');
      Cookies.remove('login_token');
    } catch (error) {
      // Best-effort cleanup; continue to next steps.
    }

    if (hasSsoToken && authconfig && authconfig.logout_url) {
      window.location.href = authconfig.logout_url;
      return;
    }

    setIsVerified(false);
    setVerifiedMember(null);
    setMemberData(null);
    setMemberError('');
    setEnrolmentInput('');
    setOtpSent(false);
    setOtpCode('');
    setOtpError('');
    setOtpExpiresAt('');
    setIsModalOpen(false);
    setIsLoginModalOpen(false);
    setBookingMessage('');
    setBookingError('');
    setPaymentStatus(null);
    setPaymentUrl('');
    setPaymentReference('');
    setPaymentInfoByEvent({});
    setEvents([]);
  };

  const showLogout = Boolean(memberId || isVerified || verifiedMember);

  return (
    <Fragment>

      <section className="council-members-page">
        <div className="container">
          <div className="booking-shell">
            <div className="booking-header">
              <div className="booking-header-row">
                <h1>SAFA International Conference 2026 Registration</h1>
                {showLogout && (
                  <button
                    className="btn btn-light booking-logout"
                    type="button"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>

            {!memberId && !isVerified && (
              <div className="booking-alert booking-alert-warning">
                {/* <h4>Sign In Required</h4> */}
                <p>Register with your enrolment number.</p>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => setIsLoginModalOpen(true)}
                >
                  Login with OTP
                </button>
              </div>
            )}

            {memberId && !isVerified && (
              <div className="booking-alert booking-alert-info">
                <h4>Identity Verification</h4>
                <p>Complete OTP verification to unlock conference registration.</p>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                >
                  Verify Now
                </button>
              </div>
            )}

            {isVerified && (
              <div className="booking-section">
                <h3>Open Registration</h3>
                {paymentStatus && (
                  <div className="booking-payment-status">
                    <h4>Payment Status</h4>
                    {paymentInfoLoading && (
                      <p className="booking-muted">Refreshing payment status...</p>
                    )}
                    {paymentInfoError && <p className="booking-error">{paymentInfoError}</p>}
                    {!paymentInfoLoading && !paymentInfoError && (
                      <p className="booking-success">
                        {paymentStatus.message || 'Payment processed.'}
                      </p>
                    )}
                    <div className="booking-status-grid">
                      <p>
                        <strong>Status:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.payment_status) ||
                          paymentStatus.status}
                      </p>
                      <p>
                        <strong>Booking:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.booking_status) ||
                          paymentStatus.bookingStatus}
                      </p>
                      <p>
                        <strong>Payment State:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.payment_state) ||
                          paymentStatus.paymentState}
                      </p>
                      <p>
                        <strong>Amount:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.currency) ||
                          paymentStatus.currency}{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.paid_amount) ||
                          paymentStatus.paidAmount}
                      </p>
                      <p>
                        <strong>Transaction ID:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.transaction_id) ||
                          paymentStatus.transactionId}
                      </p>
                      <p>
                        <strong>Participant ID:</strong>{' '}
                        {(paymentInfoByEvent[paymentStatus.eventId] &&
                          paymentInfoByEvent[paymentStatus.eventId].participant &&
                          paymentInfoByEvent[paymentStatus.eventId].participant.id) ||
                          paymentStatus.participantId}
                      </p>
                    </div>
                    {paymentStatus.eventId && getPaymentCta(paymentStatus.eventId) && (
                      <div className="booking-payment-cta">
                        <p className="booking-muted">
                          Payment is pending. Complete payment to confirm your registration.
                        </p>
                        <a
                          className="btn btn-primary"
                          href={getPaymentCta(paymentStatus.eventId).sessionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getPaymentCta(paymentStatus.eventId).label}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {eventsLoading && <p className="booking-muted">Loading events...</p>}
                {eventsError && <p className="booking-error">{eventsError}</p>}
                {bookingError && <p className="booking-error">{bookingError}</p>}
                {bookingMessage && <p className="booking-success">{bookingMessage}</p>}
                {paymentUrl && (
                  <div className="booking-payment">
                    <p className="booking-muted">
                      If you are not redirected, continue to payment manually.
                    </p>
                    {paymentReference && (
                      <p className="booking-muted">Transaction ID: {paymentReference}</p>
                    )}
                    <a
                      className="btn btn-primary"
                      href={paymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Proceed to Payment
                    </a>
                  </div>
                )}
                {!eventsLoading && !eventsError && events.length === 0 && (
                  <p className="booking-muted">No open registration options are available.</p>
                )}
                <div className="booking-grid">
                  {events.map((eventItem) => (
                    <div className="booking-card" key={eventItem.id}>
                      {paymentInfoByEvent[eventItem.id] &&
                        paymentInfoByEvent[eventItem.id].participant && (
                          <div className="booking-card-banner">
                            {paymentInfoByEvent[eventItem.id].participant.payment_status === 'PAID'
                              ? 'Registration Confirmed'
                              : 'Registration Submitted'}
                          </div>
                        )}
                      <div className="booking-card-header">
                        <span className="booking-tag">{eventItem.event_type}</span>
                        <span className="booking-code">{eventItem.code}</span>
                      </div>
                      <h4>{eventItem.name}</h4>
                      <p className="booking-meta">
                        {eventItem.venue} | {formatEventDate(eventItem.start_date)} -{' '}
                        {formatEventDate(eventItem.end_date)}
                      </p>
                      <div className="booking-fee">
                        <span>Registration Fee</span>
                        <strong>
                          {eventItem.currency} {eventItem.fee_amount}
                        </strong>
                      </div>
                      <div className="booking-status">
                        <span>{eventItem.status}</span>
                        {eventItem.payment_required ? (
                          <span className="booking-status-required">Payment Required</span>
                        ) : (
                          <span className="booking-status-free">No Payment</span>
                        )}
                      </div>
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => handleBookEvent(eventItem)}
                        disabled={
                          bookingLoadingId === eventItem.id ||
                          (paymentInfoByEvent[eventItem.id] &&
                            paymentInfoByEvent[eventItem.id].participant &&
                            (paymentInfoByEvent[eventItem.id].participant.booking_status === 'CONFIRMED' ||
                              paymentInfoByEvent[eventItem.id].participant.payment_state === 'INITIATED' ||
                              paymentInfoByEvent[eventItem.id].participant.payment_state === 'FAILED' ||
                              paymentInfoByEvent[eventItem.id].participant.payment_state === 'CANCELLED'))
                        }
                      >
                        {bookingLoadingId === eventItem.id
                          ? 'Submitting...'
                          : paymentInfoByEvent[eventItem.id] &&
                            paymentInfoByEvent[eventItem.id].participant &&
                            paymentInfoByEvent[eventItem.id].participant.booking_status === 'CONFIRMED'
                          ? 'Already Registered'
                          : paymentInfoByEvent[eventItem.id] &&
                            paymentInfoByEvent[eventItem.id].participant &&
                            (paymentInfoByEvent[eventItem.id].participant.payment_state === 'INITIATED' ||
                              paymentInfoByEvent[eventItem.id].participant.payment_state === 'FAILED' ||
                              paymentInfoByEvent[eventItem.id].participant.payment_state === 'CANCELLED')
                          ? 'Payment Required'
                          : 'Continue Registration'}
                      </button>
                      {getPaymentCta(eventItem.id) && (
                        <a
                          className="btn btn-secondary"
                          href={getPaymentCta(eventItem.id).sessionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getPaymentCta(eventItem.id).label}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {isModalOpen && memberId && (
        <div className="booking-modal-overlay" role="dialog" aria-modal="true">
          <div className="booking-modal">
            <div className="booking-modal-header">
              <h3>Verify Your ICAB Membership</h3>
              <button
                className="booking-modal-close"
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close verification modal"
              >
                &times;
              </button>
            </div>

            <div className="booking-modal-body">
              <p className="booking-muted">
                Enter your 4 digit enrolment number to retrieve your ICAB profile and
                receive an OTP.
              </p>

              <form className="booking-form" onSubmit={handleFetchMember}>
                <label htmlFor="enrolmentInput">Enrolment No (4 digits)</label>
                <input
                  id="enrolmentInput"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength="4"
                  value={enrolmentInput}
                  onChange={(event) =>
                    setEnrolmentInput(event.target.value.replace(/[^0-9]/g, ''))
                  }
                  placeholder="Enter last 4 digits"
                  required
                />
                {memberError && <p className="booking-error">{memberError}</p>}
                <button className="btn btn-primary" type="submit" disabled={memberLoading}>
                  {memberLoading ? 'Searching...' : 'Find Member'}
                </button>
              </form>

              {memberData && !otpSent && (
                <div className="booking-member-card">
                  <h4>Member Information</h4>
                  <p>
                    <strong>Name:</strong> {memberData.name}
                  </p>
                  <p>
                    <strong>Enrolment No:</strong> {memberData.enrolmentNo}
                  </p>
                  <p>
                    <strong>Email:</strong> {memberData.email}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {memberData.mobile}
                  </p>
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => {
                      setMemberData(null);
                      setEnrolmentInput('');
                      setMemberError('');
                      setOtpSent(false);
                      setOtpError('');
                      setOtpExpiresAt('');
                    }}
                  >
                    Use Different Enrolment No
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending}
                  >
                    {otpSending ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                  {otpExpiresAt && (
                    <p className="booking-muted">OTP expires at {otpExpiresAt}</p>
                  )}
                </div>
              )}
              {memberData && otpSent && (
                <form className="booking-form" onSubmit={handleVerifyOtp}>
                  <label htmlFor="loginOtpInput">Enter OTP</label>
                  <input
                    id="loginOtpInput"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4,6}"
                    maxLength="6"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Enter OTP"
                    required
                  />
                  {otpError && <p className="booking-error">{otpError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={otpVerifying}>
                    {otpVerifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtpCode('');
                      setOtpError('');
                    }}
                  >
                    Back
                  </button>
                </form>
              )}

              {otpSent && memberData && (
                <form className="booking-form" onSubmit={handleVerifyOtp}>
                  <label htmlFor="otpInput">Enter OTP</label>
                  <input
                    id="otpInput"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4,6}"
                    maxLength="6"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Enter OTP"
                    required
                  />
                  {otpError && <p className="booking-error">{otpError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={otpVerifying}>
                    {otpVerifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {isLoginModalOpen && !memberId && (
        <div className="booking-modal-overlay" role="dialog" aria-modal="true">
          <div className="booking-modal">
            <div className="booking-modal-header">
              <h3>Sign In to Continue</h3>
              <button
                className="booking-modal-close"
                type="button"
                onClick={() => setIsLoginModalOpen(false)}
                aria-label="Close login modal"
              >
                &times;
              </button>
            </div>
            <div className="booking-modal-body">
              {!memberData && (
                <form className="booking-form" onSubmit={handleFetchMember}>
                  <label htmlFor="loginEnrolmentInput">Enrolment No (4 digits)</label>
                  <input
                    id="loginEnrolmentInput"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength="4"
                    value={enrolmentInput}
                    onChange={(event) =>
                      setEnrolmentInput(event.target.value.replace(/[^0-9]/g, ''))
                    }
                    placeholder="Enter 4 digits"
                    required
                  />
                  {memberError && <p className="booking-error">{memberError}</p>}
                  <button className="btn btn-secondary" type="submit" disabled={memberLoading}>
                    {memberLoading ? 'Searching...' : 'Find Member'}
                  </button>
                </form>
              )}
              {memberData && !otpSent && (
                <div className="booking-member-card">
                  <h4>Member Information</h4>
                  <p>
                    <strong>Name:</strong> {memberData.name}
                  </p>
                  <p>
                    <strong>Enrolment No:</strong> {memberData.enrolmentNo}
                  </p>
                  <p>
                    <strong>Email:</strong> {memberData.email}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {memberData.mobile}
                  </p>
                  <Link
                    className="btn btn-light"
                    type="button"
                    onClick={() => {
                      setMemberData(null);
                      setEnrolmentInput('');
                      setMemberError('');
                      setOtpSent(false);
                      setOtpError('');
                      setOtpExpiresAt('');
                    }}
                  >
                    Back
                  </Link>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending}
                  >
                    {otpSending ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                  {otpError && <p className="booking-error">{otpError}</p>}
                  {otpExpiresAt && (
                    <p className="booking-muted">OTP expires at {otpExpiresAt}</p>
                  )}
                </div>
              )}
              {memberData && otpSent && (
                <form className="booking-form" onSubmit={handleVerifyOtp}>
                  <label htmlFor="loginOtpInput">Enter OTP</label>
                  <input
                    id="loginOtpInput"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4,6}"
                    maxLength="6"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Enter OTP"
                    required
                  />
                  {otpError && <p className="booking-error">{otpError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={otpVerifying}>
                    {otpVerifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtpCode('');
                      setOtpError('');
                    }}
                  >
                    Back
                  </button>
                </form>
              )}
              <div className="booking-form">
                <Link
                  className="btn btn-default"
                  type="button"
                  onClick={() => setIsLoginModalOpen(false)}
                >
                  Not Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default SafaBooking;
